<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="hero" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex items-center lg:mt-[4rem] lg:-mb-[2rem]">
        <div class="flex flex-col gap-y-4.5 lg:gap-y-12">
          <h1
            class="text-[32px] leading-[36px] lg:text-[70px] lg:leading-[74px] font-bold text-dark text-center font-markPro lg:max-w-[20ch] tracking-[-0.1rem]">
            {{ t('pages.info.support.hero.heading.1') }}
            <span class="text-gradient-primary whitespace-nowrap">{{
              t('pages.info.support.hero.heading.2')
            }}</span>
            {{ t('pages.info.support.hero.heading.3') }}
          </h1>

          <div class="flex flex-col text-center leading-[27px] lg:text-lg font-semibold">
            <p class="text-lighter">
              {{ t('pages.info.support.hero.description.1') }}
            </p>
            <p class="text-dark">
              {{ t('pages.info.support.hero.description.2') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="key-features">
      <div class="grid lg:grid-cols-3 gap-8">
        <div
          class="md:col-span-2 flex flex-col gap-y-10 lg:gap-y-20 rounded-xl bg-white p-4 lg:p-8 lg:!pb-11 drop-shadow">
          <div class="flex flex-col gap-y-0 lg:gap-y-1">
            <h4
              class="font-markPro text-gradient-primary text-xl lg:text-[26px] font-bold tracking-[-0.03rem]">
              {{ t('pages.info.support.cards.helpcenter.description') }}
            </h4>
            <h5
              class="font-markPro font-bold tracking-[-0.1rem] text-3xl lg:text-[40px] max-lg:py-4">
              {{ t('pages.info.support.cards.helpcenter.title') }}
            </h5>

            <p class="text-lighter pt-[0.2rem]">
              {{ t('pages.info.support.cards.helpcenter.text') }}
            </p>
          </div>

          <div class="flex max-lg:flex-col gap-4">
            <a
              href="https://support.bistroo.com"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-primary !py-2.5 !px-7">
              {{ t('pages.info.support.cards.helpcenter.actions.helpcenter') }}
            </a>
            <RouterLink :to="{ name: 'info.faq' }" class="btn btn-light !py-2.5 !px-7">
              {{ t('pages.info.support.cards.helpcenter.actions.faq') }}
            </RouterLink>
          </div>
        </div>

        <div
          class="flex flex-col lg:gap-y-8 rounded-xl bg-white p-4 lg:p-8 pb-8 lg:!pb-12 drop-shadow justify-between">
          <div class="flex flex-col gap-y-0 lg:gap-y-1">
            <h4
              class="font-markPro text-gradient-primary text-xl lg:text-[26px] font-bold tracking-[-0.03rem]">
              {{ t('pages.info.support.cards.demo.description') }}
            </h4>
            <h5
              class="font-markPro font-bold tracking-[-0.1rem] text-3xl lg:text-[40px] max-lg:py-2">
              {{ t('pages.info.support.cards.demo.title') }}
            </h5>
            <p class="text-lighter pt-[0.2rem]">
              {{ t('pages.info.support.cards.demo.text') }}
            </p>
          </div>

          <div>
            <p class="font-semibold pt-[1.5rem] pb-[1.5rem]">
              {{ t('pages.info.support.cards.demo.action.title') }}
            </p>
            <a
              href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
              target="_blank"
              class="btn btn-light"
              >{{ t('pages.info.support.cards.demo.action.button') }}
            </a>
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-xl bg-white p-4 lg:p-8 drop-shadow">
          <div class="flex flex-col gap-y-0 lg:gap-y-1">
            <h4
              class="font-markPro text-gradient-primary text-xl lg:text-[26px] font-bold tracking-[-0.03rem]">
              {{ t('pages.info.support.cards.address.description') }}
            </h4>
            <h5
              class="font-markPro font-bold tracking-[-0.1rem] text-3xl lg:text-[40px] max-lg:py-2">
              {{ t('pages.info.support.cards.address.title') }}
            </h5>
            <p class="text-lighter pt-[0.2rem]">
              {{ t('pages.info.support.cards.address.text') }}
            </p>

            <p class="font-semibold pt-[1.5rem]">
              {{ t('pages.info.support.cards.address.address.mail') }}
            </p>
            <p class="text-lighter">Meerenakkerplein 14<br />5652 BJ Eindhoven</p>

            <p class="font-semibold pt-[1.5rem]">
              {{ t('pages.info.support.cards.address.address.visit') }}
            </p>
            <p class="text-lighter">Meerenakkerplein 14<br />5652 BJ Eindhoven</p>
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-xl bg-white p-4 lg:p-8 drop-shadow">
          <div class="flex flex-col gap-y-0 lg:gap-y-1">
            <h4
              class="font-markPro text-gradient-primary text-xl lg:text-[26px] font-bold tracking-[-0.03rem]">
              {{ t('pages.info.support.cards.contact.description') }}
            </h4>
            <h5
              class="font-markPro font-bold tracking-[-0.1rem] text-3xl lg:text-[40px] max-lg:py-2">
              {{ t('pages.info.support.cards.contact.title') }}
            </h5>
            <p class="text-lighter pt-[0.2rem]">
              {{ t('pages.info.support.cards.contact.text') }}
            </p>

            <p class="font-semibold pt-[1.5rem] pb-[1rem]">Chat support</p>
            <a href="#" class="btn btn-light">Open chat</a>

            <p class="font-semibold pt-[2rem]">Whatsapp & Tel.</p>
            <p class="text-lighter font-semibold pb-[1rem]">(+31) 085 025 02 05</p>
            <a href="https://wa.me/+31850250205" target="_blank" class="btn btn-light"
              >Open whatsapp</a
            >

            <p class="font-semibold pt-[2rem]">Email</p>
            <p class="text-lighter font-semibold pb-[1rem]">info@bistroo.nl</p>
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-xl bg-white p-4 lg:p-8 drop-shadow">
          <div class="flex flex-col gap-y-0 lg:gap-y-1">
            <h4
              class="font-markPro text-gradient-primary text-xl lg:text-[26px] font-bold tracking-[-0.03rem]">
              {{ t('pages.info.support.cards.partners.description') }}
            </h4>
            <h5
              class="font-markPro font-bold tracking-[-0.1rem] text-3xl lg:text-[40px] max-lg:py-2">
              {{ t('pages.info.support.cards.partners.title') }}
            </h5>
            <p class="text-lighter pt-[0.2rem]">
              {{ t('pages.info.support.cards.partners.text') }}
            </p>

            <p class="font-semibold pt-[1.5rem] pb-[1rem]">
              {{ t('pages.info.support.cards.partners.action.title') }}
            </p>
            <a href="#" class="btn btn-light">Coming soon</a>
          </div>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />

            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t } = useI18n()
useHead(computed(() => ({
  title: t('pages.info.support.seo.title'),
  meta: [{ name: 'description', content: t('pages.info.support.seo.description') }],
})))
</script>

<style scoped></style>
